import { call, fork, put, race, select, take } from "redux-saga/effects";
import { demonstrateHotspotPathAnimationDurationMs } from "../../config";
import { setCurrentFrameIndex } from "../hotspotEditor/hotspotEditorReducers";
import {
  getBounds,
  getInvisibleFrames,
  getKeyFrameSequentialIndices,
  getVisibleFramesInLayer,
} from "../selectors";
import {
  beadStates,
  demoHotspotPath,
  setBeadState,
  showHotspotPath,
} from "../ui/ui";
import { demonstrateHotspotPath } from "./sagasActions";
import { eventChannel } from "redux-saga";
import { range } from "lodash";

// Utility function to create an event channel for browser animation frames
function createAnimationFrameChannel() {
  return eventChannel((emit) => {
    let start;
    const handleAnimationFrame = (timestamp) => {
      if (start === undefined) {
        start = timestamp;
      }
      emit(timestamp - start);
      requestAnimationFrame(handleAnimationFrame);
    };

    // Start the animation frame loop
    const animationFrameLoop = requestAnimationFrame(handleAnimationFrame);

    // Return the unsubscribe function
    return () => {
      cancelAnimationFrame(animationFrameLoop);
    };
  });
}

function* constantDurationAnimation(options) {
  const { duration, frameSaga, frameCount } = options;
  const channel = yield createAnimationFrameChannel();
  let animationProgress = 0;
  let previousFrameIndex = -1;
  let frameIndex = 0;
  try {
    while (animationProgress < 1) {
      // Take the next animation frame event from the channel
      const elapsed = yield take(channel);

      animationProgress = Math.min(elapsed / duration, 1);
      frameIndex = Math.round(animationProgress * (frameCount - 1));

      if (frameIndex > previousFrameIndex) {
        yield fork(frameSaga, { frameIndex, previousFrameIndex });
        previousFrameIndex = frameIndex;
      }
    }
  } finally {
    channel.close();
  }
}

// Utility function to create an event channel for keyboard events
const createUserInteractionEventChannel = () => {
  return eventChannel((emit) => {
    const handleKeyPress = (event) => {
      emit("INTERACTED");
    };

    // Add event listeners
    window.addEventListener("keydown", handleKeyPress);
    window.addEventListener("pointerdown", handleKeyPress, true);

    // Return the unsubscribe function
    return () => {
      // Remove event listeners when the channel is closed or the saga is cancelled
      window.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("pointerdown", handleKeyPress, true);
    };
  });
};

function* rewind(options) {
  const { frameArray } = options;
  if (!frameArray.length) {
    return;
  }
  function* frameSaga({ frameIndex }) {
    yield put(setCurrentFrameIndex({ frameIndex: frameArray[frameIndex] }));
  }
  yield constantDurationAnimation({
    duration: demonstrateHotspotPathAnimationDurationMs,
    frameSaga,
    frameCount: frameArray.length,
  });
}

function* play(options) {
  const { frameArray } = options;
  if (!frameArray.length) {
    return;
  }
  function* frameSaga({ frameIndex, previousFrameIndex }) {
    yield put(setCurrentFrameIndex({ frameIndex: frameArray[frameIndex] }));
    // when skipping frame we have to set all beads from the previous to current
    // to be visible, for this we calculate this range
    const indexRange = range(previousFrameIndex, frameIndex + 1).map(
      (i) => frameArray[i]
    );
    yield put(
      setBeadState({
        frameArray: indexRange,
        value: beadStates.visible,
      })
    );
  }
  yield constantDurationAnimation({
    duration: demonstrateHotspotPathAnimationDurationMs,
    frameSaga,
    frameCount: frameArray.length,
  });
}

// This generator is a saga for redux-saga
export function* demonstrateHotspotPathWorker() {
  try {
    yield put(demoHotspotPath());
    const boundaries = yield select(getBounds);
    const keyFrames = yield select(getKeyFrameSequentialIndices);
    const direction = keyFrames[0] === boundaries[0] ? 1 : -1;
    const visibleFrames = yield select(getVisibleFramesInLayer);
    const invisibleFrames = yield select(getInvisibleFrames);
    if (direction === -1) {
      visibleFrames.reverse();
      invisibleFrames.reverse();
    }
    yield put(
      setBeadState({
        frameArray: [...invisibleFrames, ...visibleFrames],
        value: beadStates.invisible,
      })
    );
    yield rewind({ frameArray: invisibleFrames });
    yield play({ frameArray: visibleFrames });
  } finally {
    yield put(showHotspotPath());
  }
}

export function* demonstrateHotspotPathWatcher() {
  const channel = yield createUserInteractionEventChannel();
  while (true) {
    yield take(demonstrateHotspotPath().type);
    yield race({
      task: call(demonstrateHotspotPathWorker),
      userInteraction: take(channel),
    });
  }
}
